import React, { useEffect, useRef, useState } from "react";
import {
  Plug, CheckCircle2, AlertTriangle, Loader2, ExternalLink, Send, Trash2,
  Globe2, Newspaper, Save, RefreshCw, X as XIcon, Calendar, Clock, Upload, Image as ImageIcon, Zap, Play,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import BulkBatchTab from "@/components/blog/BulkBatchTab";
import DraftsTab from "@/components/blog/DraftsTab";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { appConfirm } from "@/components/AppDialogHost";

/**
 * BlogConnector — connect a station's WordPress site and publish AI news
 * stories directly to it. Ghost, Webflow, Wix etc. are listed as "coming soon".
 */
const timeAgo = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export default function BlogConnector() {
  const [platforms, setPlatforms] = useState([]);
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("connection"); // connection | publish | history
  const [wpStatus, setWpStatus] = useState(null);
  const [rechecking, setRechecking] = useState(false);

  const loadStatus = (force = false) =>
    api.get(`/blog-connector/wordpress/status${force ? "?force=1" : ""}`)
      .then((r) => setWpStatus(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/blog-connector/platforms").then((r) => setPlatforms(r.data.platforms || [])),
      api.get("/blog-connector/wordpress/connection").then((r) => setConnection(r.data.connected ? r.data.connection : null)),
    ]).catch(() => {}).finally(() => setLoading(false));
    loadStatus();
  }, []);

  const recheck = async () => {
    setRechecking(true);
    try {
      const { data } = await api.get("/blog-connector/wordpress/status?force=1");
      setWpStatus(data);
      toast[data.status === "ok" ? "success" : "error"](
        data.status === "ok" ? "WordPress connection healthy" : `Connection problem: ${data.detail || "unknown"}`);
    } catch { toast.error("Couldn't check the connection"); }
    finally { setRechecking(false); }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-ink-muted text-sm">
        <Loader2 className="h-5 w-5 mx-auto animate-spin text-brand-amber mb-2" />
        Loading connectors…
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 animate-fade-up max-w-7xl mx-auto" data-testid="blog-connector-page">
      <PageHeader
        eyebrow="// BLOG PUBLISHING CONNECTOR"
        title="One-click publish to your station's blog."
        description="Connect your WordPress site once — then push every AI-generated story straight to your blog with categories, tags, featured images and SEO meta."
      />

      {/* Tabs */}
      <div className="mt-4 inline-flex rounded-lg border border-line bg-bg-elevated p-1 gap-1" data-testid="bc-tabs">
        {[
          { id: "connection", label: "Connection" },
          { id: "publish",    label: "Publish a story" },
          { id: "auto",       label: "Auto-Publish" },
          { id: "bulk",       label: "Bulk Batch" },
          { id: "drafts",     label: "Drafts" },
          { id: "history",    label: "Publish history" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`bc-tab-${t.id}`}
            className={`px-4 py-1.5 text-xs font-mono uppercase tracking-wider rounded-md transition ${tab === t.id ? "bg-brand-amber text-black font-semibold" : "text-ink-secondary hover:text-ink-primary"}`}
          >{t.label}</button>
        ))}
      </div>

      {wpStatus?.connected && (
        <div
          data-testid="bc-status-strip"
          className={`mt-3 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
            wpStatus.status === "ok" ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`}
        >
          <span className={`h-2 w-2 rounded-full shrink-0 ${wpStatus.status === "ok" ? "bg-emerald-400 animate-pulse" : "bg-rose-400"}`} />
          {wpStatus.status === "ok" ? (
            <span className="font-semibold text-emerald-300" data-testid="bc-status-ok">WordPress connected &amp; healthy</span>
          ) : (
            <span className="font-semibold text-rose-300" data-testid="bc-status-problem">WordPress connection problem</span>
          )}
          <span className="text-ink-muted truncate">
            {(wpStatus.site_url || "").replace(/^https?:\/\//, "")} · as {wpStatus.username}
          </span>
          {wpStatus.status !== "ok" && wpStatus.detail && (
            <span className="text-rose-300/90 truncate">— {wpStatus.detail}</span>
          )}
          <span className="text-ink-muted ml-auto shrink-0">checked {timeAgo(wpStatus.checked_at)}</span>
          <button
            onClick={recheck}
            disabled={rechecking}
            data-testid="bc-status-recheck"
            className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary disabled:opacity-50 shrink-0"
          >
            {rechecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Re-check
          </button>
        </div>
      )}

      {tab === "connection" && (
        <ConnectionPanel
          platforms={platforms}
          connection={connection}
          onConnected={(c) => { setConnection(c); loadStatus(true); }}
          onDisconnected={() => { setConnection(null); setWpStatus({ connected: false }); }}
        />
      )}
      {tab === "publish" && (
        connection
          ? <PublishPanel connection={connection} />
          : <NeedsConnection onGo={() => setTab("connection")} />
      )}
      {tab === "auto" && (
        connection
          ? <AutoPublishPanel />
          : <NeedsConnection onGo={() => setTab("connection")} />
      )}
      {tab === "bulk" && <BulkBatchTab />}
      {tab === "drafts" && <DraftsTab />}
      {tab === "history" && <HistoryPanel />}
    </div>
  );
}

function NeedsConnection({ onGo }) {
  return (
    <div className="mt-8 rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center" data-testid="bc-needs-connection">
      <AlertTriangle className="h-6 w-6 mx-auto text-cyan-300 mb-2" />
      <div className="text-base font-bold text-ink-primary">Connect a blog first</div>
      <div className="text-xs text-ink-muted mt-1">You haven&apos;t connected a WordPress site yet.</div>
      <button onClick={onGo} className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black" data-testid="bc-go-connection">
        <Plug className="h-3.5 w-3.5" /> Go to connection setup
      </button>
    </div>
  );
}

const TZ_OPTIONS = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "Europe/London", "Europe/Amsterdam", "Europe/Berlin",
  "Europe/Paris", "UTC",
];

const STORY_CATEGORIES = ["music", "entertainment", "industry", "headlines", "sports", "local", "weather", "traffic"];

function AutoPublishPanel() {
  const [settings, setSettings] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => {
    api.get("/blog-connector/auto/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/blog-connector/auto/runs?limit=5").then((r) => setRuns(r.data || [])).catch(() => {});
  };
  useEffect(load, []);

  const save = async (patch = {}) => {
    const next = { ...settings, ...patch };
    setBusy(true);
    try {
      const { data } = await api.put("/blog-connector/auto/settings", {
        enabled: next.enabled, times: next.times, tz: next.tz, max_per_run: next.max_per_run,
        categories: next.categories || [],
      });
      setSettings(data);
      toast.success(data.enabled ? "Auto-publish is ON" : "Auto-publish saved (off)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save settings"); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/blog-connector/auto/run-now");
      const errs = (data.errors || []).length;
      toast.success(`Run complete — ${data.posted} draft${data.posted === 1 ? "" : "s"} created${errs ? `, ${errs} error(s)` : ""}`, { duration: 7000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunning(false); }
  };

  if (!settings) {
    return <div className="mt-8 text-center text-ink-muted text-sm"><Loader2 className="h-5 w-5 mx-auto animate-spin text-brand-amber mb-2" />Loading…</div>;
  }

  const setTime = (idx, val) => {
    const times = [...(settings.times || ["09:00", "16:00"])];
    times[idx] = val;
    setSettings({ ...settings, times });
  };

  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-4" data-testid="bc-auto-panel">
      <section className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber">// AUTO-PUBLISH SCHEDULE</div>
          <button
            onClick={() => save({ enabled: !settings.enabled })}
            disabled={busy}
            data-testid="bc-auto-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.enabled ? "bg-emerald-500" : "bg-line"}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <p className="text-xs text-ink-muted mb-4">
          Publishes your <span className="text-emerald-300 font-semibold">approved</span> News Center stories (last 7 days, never twice) to WordPress as
          <span className="text-ink-primary font-semibold"> drafts</span> — you review them in WordPress before they go live.
          Each story&apos;s own photo is attached automatically when one can be found.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Morning run</Label>
            <Input type="time" value={(settings.times || [])[0] || "09:00"} onChange={(e) => setTime(0, e.target.value)} className="mt-1 bg-bg border-line" data-testid="bc-auto-time-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Afternoon run</Label>
            <Input type="time" value={(settings.times || [])[1] || "16:00"} onChange={(e) => setTime(1, e.target.value)} className="mt-1 bg-bg border-line" data-testid="bc-auto-time-2" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Timezone</Label>
            <select
              value={settings.tz || "America/New_York"}
              onChange={(e) => setSettings({ ...settings, tz: e.target.value })}
              className="mt-1 w-full rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink-primary"
              data-testid="bc-auto-tz"
            >
              {TZ_OPTIONS.map((z) => <option key={z} value={z}>{z.replace("_", " ")}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Stories per run</Label>
            <select
              value={settings.max_per_run || 3}
              onChange={(e) => setSettings({ ...settings, max_per_run: parseInt(e.target.value, 10) })}
              className="mt-1 w-full rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink-primary"
              data-testid="bc-auto-max"
            >
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Up to {n}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Story categories to auto-publish</Label>
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="bc-auto-categories">
            {STORY_CATEGORIES.map((cat) => {
              const sel = (settings.categories || []).includes(cat);
              return (
                <button
                  key={cat}
                  data-testid={`bc-auto-cat-${cat}`}
                  onClick={() => {
                    const cur = settings.categories || [];
                    setSettings({ ...settings, categories: sel ? cur.filter((c) => c !== cat) : [...cur, cat] });
                  }}
                  className={`rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-wider border transition ${
                    sel
                      ? "bg-brand-amber text-black border-brand-amber font-semibold"
                      : "bg-bg text-ink-secondary border-line hover:text-ink-primary hover:border-brand-amber/50"
                  }`}
                >{cat}</button>
              );
            })}
          </div>
          <div className="text-[10px] text-ink-muted mt-1">
            {(settings.categories || []).length === 0
              ? "No categories selected — ALL approved stories will auto-publish. Pick one or more to limit your blog to those topics (e.g. a music station picks only Music)."
              : `Only approved ${settings.categories.join(" + ")} stories will auto-publish.`}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => save()}
            disabled={busy}
            data-testid="bc-auto-save"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save schedule
          </button>
          <button
            onClick={runNow}
            disabled={running}
            data-testid="bc-auto-run-now"
            className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Run now
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-ink-muted">
          <span className="inline-flex items-center gap-1.5" data-testid="bc-auto-next-run">
            <Clock className="h-3.5 w-3.5 text-brand-amber" />
            Next run: <span className="text-ink-primary font-semibold">{settings.next_run_at ? `${new Date(settings.next_run_at).toLocaleString(undefined, { timeZone: settings.tz })} (${settings.tz.split("/").pop().replace("_", " ")})` : "— (off)"}</span>
          </span>
          <span className="inline-flex items-center gap-1.5" data-testid="bc-auto-last-run">
            <Zap className="h-3.5 w-3.5 text-emerald-400" />
            Last run: <span className="text-ink-primary font-semibold">{settings.last_run_at ? `${new Date(settings.last_run_at).toLocaleString(undefined, { timeZone: settings.tz })} (${settings.tz.split("/").pop().replace("_", " ")})` : "never"}</span>
            {settings.last_run_summary && <span>· {settings.last_run_summary.posted} posted{(settings.last_run_summary.errors || []).length ? `, ${settings.last_run_summary.errors.length} error(s)` : ""}</span>}
          </span>
        </div>
      </section>

      <aside className="rounded-xl border border-line bg-bg-surface p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber mb-3">// RECENT RUNS</div>
        {runs.length === 0 && <div className="text-xs text-ink-muted" data-testid="bc-auto-no-runs">No runs yet.</div>}
        <div className="space-y-2">
          {runs.map((r) => (
            <div key={r.run_id} className="rounded-md border border-line bg-bg-elevated px-3 py-2" data-testid={`bc-auto-run-${r.run_id}`}>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-ink-primary font-semibold">{r.posted} draft{r.posted === 1 ? "" : "s"}{r.manual ? " · manual" : ""}</span>
                <span className="text-ink-muted">{new Date(r.at).toLocaleString()}</span>
              </div>
              {(r.items || []).map((it) => (
                <a key={it.post_id} href={it.link} target="_blank" rel="noreferrer" className="block truncate text-[11px] text-cyan-300 hover:underline mt-0.5">
                  {it.headline}
                </a>
              ))}
              {(r.errors || []).map((er, i) => (
                <div key={i} title={er} className="text-[10px] text-rose-400 mt-0.5 truncate">{er}</div>
              ))}
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

function ConnectionPanel({ platforms, connection, onConnected, onDisconnected }) {
  const [form, setForm] = useState({
    site_url: connection?.site_url || "",
    username: connection?.username || "",
    app_password: "",
    default_status: connection?.default_status || "draft",
  });
  const [busy, setBusy] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testReport, setTestReport] = useState(null);

  const connect = async () => {
    if (!form.site_url || !form.username || !form.app_password) {
      toast.error("Please fill all 3 fields"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/blog-connector/wordpress/connect", form);
      onConnected(data.connection);
      toast.success(`Connected as ${data.connection.remote_user_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed", { duration: 8000 });
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    if (!form.site_url || !form.username || !form.app_password) {
      toast.error("Fill all 3 fields to run the diagnostic"); return;
    }
    setTesting(true); setTestReport(null);
    try {
      const { data } = await api.post("/blog-connector/wordpress/test", form);
      setTestReport(data);
      if (data.ok) toast.success("All checks passed — ready to connect");
      else toast.error("Diagnostic found a problem — see report below", { duration: 6000 });
    } catch (e) {
      setTestReport({ ok: false, steps: [{ name: "Request", status: "fail", note: e?.response?.data?.detail || e?.message || "Unknown error" }] });
      toast.error(e?.response?.data?.detail || "Diagnostic failed", { duration: 8000 });
    } finally { setTesting(false); }
  };

  const disconnect = async () => {
    if (!await appConfirm("Disconnect WordPress? Your scheduled posts will be cancelled.")) return;
    setDisconnecting(true);
    try {
      await api.delete("/blog-connector/wordpress/connection");
      onDisconnected();
      setForm((f) => ({ ...f, app_password: "" }));
      toast.success("Disconnected");
    } catch { toast.error("Failed to disconnect"); }
    finally { setDisconnecting(false); }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-4" data-testid="bc-connection">
      {/* Connection card */}
      <section className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber">// WORDPRESS</div>
            <div className="font-display text-lg font-bold mt-0.5">Connect with Application Password</div>
            <div className="text-[11px] text-ink-muted mt-0.5">
              In WP-Admin → <strong>Users → Profile</strong>, scroll to <em>Application Passwords</em> and generate one named &ldquo;BroadcastFlow AI&rdquo;.
              {" "}
              <a href="https://wordpress.org/documentation/article/application-passwords/" target="_blank" rel="noreferrer" className="text-brand-amber hover:underline">
                Docs <ExternalLink className="h-3 w-3 inline" />
              </a>
            </div>
          </div>
          {connection && (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-mono text-emerald-300">
              <CheckCircle2 className="h-3 w-3" /> CONNECTED
            </span>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">WordPress site URL</Label>
            <Input
              value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })}
              placeholder="https://yourstation.com"
              className="mt-1 bg-bg border-line"
              data-testid="bc-site-url"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">WP username</Label>
            <Input
              value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="admin"
              className="mt-1 bg-bg border-line"
              data-testid="bc-username"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Application Password</Label>
            <Input
              type="password"
              value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })}
              placeholder="abcd EFGH 1234 ijkl MNOP 5678  (24 chars, spaces OK)"
              className="mt-1 bg-bg border-line font-mono"
              data-testid="bc-app-password"
            />
            <div className="text-[10px] text-ink-muted mt-1">
              We store this securely scoped to your account. Revoke anytime in WP-Admin.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Default publish status</Label>
            <Select value={form.default_status} onValueChange={(v) => setForm({ ...form, default_status: v })}>
              <SelectTrigger className="bg-bg border-line mt-1" data-testid="bc-default-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft" className="text-xs">Draft (manual approval)</SelectItem>
                <SelectItem value="publish" className="text-xs">Publish immediately</SelectItem>
                <SelectItem value="future" className="text-xs">Scheduled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <button
            onClick={connect}
            disabled={busy}
            data-testid="bc-connect-btn"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black amber-glow disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
            {connection ? "Update connection" : "Connect"}
          </button>
          <button
            onClick={testConnection}
            disabled={testing || busy}
            data-testid="bc-test-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50"
          >
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Test connection
          </button>
          {connection && (
            <button
              onClick={disconnect}
              disabled={disconnecting}
              data-testid="bc-disconnect-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-300 hover:bg-rose-500/20 disabled:opacity-50"
            >
              {disconnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Disconnect
            </button>
          )}
        </div>

        {testReport && (
          <div
            className={`mt-3 rounded-lg border p-3 ${testReport.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`}
            data-testid="bc-test-report"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">// DIAGNOSTIC REPORT</div>
              <button onClick={() => setTestReport(null)} className="text-ink-muted hover:text-ink-primary" data-testid="bc-test-close">
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
            <ul className="space-y-1.5 text-xs">
              {testReport.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`bc-test-step-${i}`}>
                  {s.status === "pass" && <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />}
                  {s.status === "warn" && <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />}
                  {s.status === "fail" && <XIcon className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <div className="font-semibold text-ink-primary">{s.name}</div>
                    {s.note && <div className="text-[11px] text-ink-muted mt-0.5 whitespace-pre-wrap break-words">{s.note}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Other platforms list */}
      <aside className="rounded-xl border border-line bg-bg-surface p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber mb-3">// OTHER PLATFORMS</div>
        <div className="space-y-1.5">
          {platforms.map((p) => (
            <div
              key={p.id}
              data-testid={`bc-platform-${p.id}`}
              className="flex items-center justify-between rounded-md border border-line bg-bg-elevated px-3 py-2"
            >
              <span className="text-xs font-semibold text-ink-primary">{p.label}</span>
              <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
                p.status === "available"
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                  : "bg-bg text-ink-muted border border-line"
              }`}>{p.status === "available" ? "READY" : "SOON"}</span>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

function PublishPanel({ connection }) {
  const [stories, setStories] = useState([]);
  const pickRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({
    headline: "", content_html: "", excerpt: "",
    tags: "", seo_keywords: "",
    featured_image_url: "",
    featured_image_b64: "",
    featured_image_name: "",
    status: connection.default_status || "draft",
    scheduled_for: "",
    categories: [],
  });
  const [tax, setTax] = useState({ categories: [], tags: [] });
  const [busy, setBusy] = useState(false);
  const [loadingTax, setLoadingTax] = useState(true);

  useEffect(() => {
    api.get("/news/stories?status=approved&limit=100")
      .then((r) => setStories(r.data || []))
      .catch(() => {});
    api.get("/blog-connector/wordpress/taxonomies")
      .then((r) => setTax(r.data))
      .catch(() => {})
      .finally(() => setLoadingTax(false));
  }, []);

  const pickStory = async (id) => {
    setSelectedId(id);
    pickRef.current = id;
    if (!id) {
      setForm((f) => ({ ...f, headline: "", content_html: "", excerpt: "", tags: "", seo_keywords: "" }));
      return;
    }
    // Clear any previous story's image so it never leaks onto the new pick
    setForm((f) => ({ ...f, featured_image_url: "", featured_image_b64: "", featured_image_name: "" }));
    try {
      const { data } = await api.get(`/news/stories/${id}`);
      const blog = (data.generated || {}).blog || {};
      setForm((f) => ({
        ...f,
        headline: blog.headline || data.headline || "",
        content_html: blog.article || data.summary || "",
        excerpt: data.summary || "",
        tags: ((blog.tags || []).join(", ")),
        seo_keywords: ((blog.seo_keywords || []).join(", ")),
      }));
      // Auto-pull the story's own photo as the featured image
      api.post(`/news/stories/${id}/resolve-image`).then(({ data: img }) => {
        if (img.image_url && pickRef.current === id) {
          setForm((f) => ({ ...f, featured_image_url: img.image_url, featured_image_b64: "", featured_image_name: "Photo from story" }));
          toast.success("Story photo attached as featured image");
        }
      }).catch(() => {});
    } catch { toast.error("Couldn't load story details"); }
  };

  const onPickImageFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image is too large (max 8MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, featured_image_b64: reader.result, featured_image_name: file.name, featured_image_url: "" }));
    };
    reader.onerror = () => toast.error("Couldn't read that image");
    reader.readAsDataURL(file);
  };

  const clearImage = () => setForm((f) => ({ ...f, featured_image_b64: "", featured_image_name: "", featured_image_url: "" }));

  const publish = async () => {
    if (!form.headline || !form.content_html) { toast.error("Headline and content are required"); return; }
    if (form.status === "future" && !form.scheduled_for) { toast.error("Pick a scheduled date for Future"); return; }
    setBusy(true);
    try {
      const body = {
        story_id: selectedId || null,
        headline: form.headline,
        content_html: form.content_html,
        excerpt: form.excerpt,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        seo_keywords: form.seo_keywords.split(",").map((s) => s.trim()).filter(Boolean),
        featured_image_b64: form.featured_image_b64 || null,
        featured_image_url: form.featured_image_b64 ? null : (form.featured_image_url || null),
        categories: form.categories,
        status: form.status,
        scheduled_for: form.status === "future" ? new Date(form.scheduled_for).toISOString() : null,
      };
      const { data } = await api.post("/blog-connector/wordpress/publish", body);
      toast.success(`Published · ${data.post.status} · #${data.post.id}`, { duration: 6000 });
      // Reset
      setSelectedId(null);
      setForm({ headline: "", content_html: "", excerpt: "", tags: "", seo_keywords: "", featured_image_url: "", featured_image_b64: "", featured_image_name: "", status: connection.default_status || "draft", scheduled_for: "", categories: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed", { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-4" data-testid="bc-publish">
      <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-5 space-y-3">
        {/* Source story picker */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Source from a News Center story (optional)</Label>
          <Select value={selectedId || "manual"} onValueChange={(v) => pickStory(v === "manual" ? null : v)}>
            <SelectTrigger className="bg-bg border-line mt-1" data-testid="bc-story-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manual" className="text-xs">— Manual entry (no source) —</SelectItem>
              {stories.map((s) => (
                <SelectItem key={s.story_id} value={s.story_id} className="text-xs truncate max-w-md">{s.headline}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Headline</Label>
          <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="mt-1 bg-bg border-line" data-testid="bc-headline" placeholder="SEO-friendly title ≤ 70 chars" />
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Article body (HTML or markdown)</Label>
          <Textarea value={form.content_html} onChange={(e) => setForm({ ...form, content_html: e.target.value })} rows={9} className="mt-1 bg-bg border-line text-sm" data-testid="bc-content" placeholder="Paste blog content here. Double-line-break for new paragraphs." />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Excerpt</Label>
            <Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} className="mt-1 bg-bg border-line text-xs" data-testid="bc-excerpt" placeholder="A 1–2 sentence summary" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Featured image</Label>
            {(form.featured_image_b64 || form.featured_image_url) ? (
              <div className="mt-1 relative rounded-lg border border-line overflow-hidden bg-bg" data-testid="bc-featured-preview">
                <img
                  src={form.featured_image_b64 || form.featured_image_url}
                  alt="Featured preview"
                  className="w-full h-28 object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="flex items-center justify-between px-2 py-1 text-[10px] text-ink-muted">
                  <span className="truncate">{form.featured_image_name || form.featured_image_url}</span>
                  <button onClick={clearImage} className="ml-2 inline-flex items-center gap-1 text-rose-400 hover:text-rose-300 shrink-0" data-testid="bc-featured-clear">
                    <XIcon className="h-3 w-3" /> Remove
                  </button>
                </div>
              </div>
            ) : (
              <>
                <label className="mt-1 flex items-center justify-center gap-2 rounded-lg border border-dashed border-line bg-bg px-3 py-3 text-xs text-ink-muted cursor-pointer hover:border-brand-amber hover:text-ink-primary transition-colors" data-testid="bc-featured-upload-label">
                  <Upload className="h-4 w-4" />
                  Upload from your computer
                  <input type="file" accept="image/*" className="hidden" onChange={onPickImageFile} data-testid="bc-featured-file" />
                </label>
                <div className="my-1.5 flex items-center gap-2 text-[9px] uppercase tracking-wider text-ink-muted">
                  <span className="h-px flex-1 bg-line" /> or paste a link <span className="h-px flex-1 bg-line" />
                </div>
                <Input value={form.featured_image_url} onChange={(e) => setForm({ ...form, featured_image_url: e.target.value })} className="bg-bg border-line" data-testid="bc-featured" placeholder="https://...jpg" />
              </>
            )}
            <div className="text-[10px] text-ink-muted mt-0.5">Uploaded into the WP Media Library and attached as the featured image. JPG, PNG, WebP or GIF · max 8MB.</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Tags (comma-separated)</Label>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1 bg-bg border-line" data-testid="bc-tags" placeholder="music news, pop, charts" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">SEO keywords (comma-separated)</Label>
            <Input value={form.seo_keywords} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })} className="mt-1 bg-bg border-line" data-testid="bc-seo" placeholder="seo, keywords, here" />
          </div>
        </div>

        {tax.categories.length > 0 && (
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Categories</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tax.categories.map((c) => {
                const on = form.categories.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => setForm((f) => ({ ...f, categories: on ? f.categories.filter((x) => x !== c.id) : [...f.categories, c.id] }))}
                    data-testid={`bc-cat-${c.id}`}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] transition ${on ? "border-brand-amber bg-brand-amber/15 text-brand-amber" : "border-line bg-bg-elevated text-ink-muted hover:text-ink-primary"}`}
                  >{c.name}</button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Right rail: status + publish */}
      <aside className="rounded-xl border border-line bg-bg-surface p-5 space-y-3 h-fit">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Publish status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="bg-bg border-line mt-1" data-testid="bc-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft" className="text-xs">Draft (manual approval)</SelectItem>
              <SelectItem value="publish" className="text-xs">Publish now</SelectItem>
              <SelectItem value="future" className="text-xs">Schedule</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.status === "future" && (
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Scheduled date &amp; time</Label>
            <Input type="datetime-local" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} className="mt-1 bg-bg border-line" data-testid="bc-scheduled" />
          </div>
        )}

        <button
          onClick={publish}
          disabled={busy || !form.headline || !form.content_html}
          data-testid="bc-publish-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
          style={{ background: "linear-gradient(135deg, #8B5CF6, #FF8A1F)" }}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {form.status === "publish" ? "Publish now" : form.status === "future" ? "Schedule" : "Save as draft"}
        </button>
        <div className="text-[10px] text-ink-muted">
          Posting to <span className="text-brand-amber">{connection.site_url}</span> as <span className="text-ink-primary">{connection.remote_user_name}</span>.
        </div>
      </aside>
    </div>
  );
}

function HistoryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/blog-connector/wordpress/history")
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="mt-6 text-center text-ink-muted text-xs"><Loader2 className="h-4 w-4 animate-spin mx-auto" /> Loading…</div>;
  if (items.length === 0) {
    return (
      <div className="mt-6 rounded-xl border border-dashed border-line p-12 text-center text-ink-muted text-sm" data-testid="bc-history-empty">
        <Newspaper className="h-5 w-5 mx-auto text-brand-amber mb-2" />
        Nothing published yet. Head to <strong className="text-ink-primary">Publish a story</strong> to send your first one.
      </div>
    );
  }
  return (
    <div className="mt-6 rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="bc-history">
      <table className="w-full text-xs">
        <thead className="bg-bg-elevated">
          <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-ink-muted">
            <th className="px-3 py-2">Title</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Published</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.publish_id} className="border-t border-line" data-testid={`bc-history-${it.publish_id}`}>
              <td className="px-3 py-2 text-ink-primary truncate max-w-md">{it.title}</td>
              <td className="px-3 py-2 capitalize text-ink-secondary">{it.status}</td>
              <td className="px-3 py-2 text-ink-muted">{new Date(it.published_at).toLocaleString()}</td>
              <td className="px-3 py-2 text-right">
                {it.remote_url && (
                  <a href={it.remote_url} target="_blank" rel="noreferrer" className="text-brand-amber hover:underline inline-flex items-center gap-1">View <ExternalLink className="h-3 w-3" /></a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
